// Files waiting to be sent, and their uploads. Lifted from the Andon app's
// `useAttachments`, on the shop floor's andon API.
//
// The STATE MACHINE is in lib/staging.ts, tested there without a DOM. This is
// the shell around it: React state, the upload calls, and the object URLs that
// have to be revoked. Nothing here decides anything.
//
// WHY UPLOADING IS NOT SENDING.
//
// The composer used to do both in one call: pick a file, and a message appeared
// saying "Somebody attached rack-empty.jpg." Four photos were four such rows,
// the words typed alongside them were a fifth, and nothing in the data said the
// five belonged together — so nothing could draw them together either.
//
// Now a pick starts its own upload immediately and lands in a tray under the
// box. Sending posts one message carrying every id. That buys three things the
// old shape could not have: progress that starts on the tap rather than four
// files queueing behind one request; a failed file that can be removed with the
// rest still sent; and a caption in the SAME bubble as the pictures it is about.
//
// AN UPLOAD THAT IS NOT SENT IS NOT VISIBLE. The bytes are on the andon in
// Odoo, but no message references them, so nothing renders. Abandoning a staged
// file leaks an orphan attachment rather than posting a sentence about a file
// nobody meant to send — which is the right way round: invisible clutter costs
// storage, where a stray line on a stopped line's thread costs attention.

import { useCallback, useEffect, useRef, useState } from 'react';
import { andonApi } from '@/api/andon/client';
import { ApiError } from '@/api/http';
import type { CapturedFile } from '@/lib/capture';
import {
  adopt as adoptOne,
  anyFailed,
  failed,
  forget as drop,
  idsOf,
  previewsOf,
  settled,
  stage,
  uploading as isUploading,
  MAX_STAGED,
  type AttachKind,
  type Staged,
} from '@/lib/staging';

export { MAX_STAGED, type Staged, type AttachKind };

/** How one upload ended. What the composer waits on for a late file. */
export interface Landed {
  key: string;
  id: number | null;
  error: string | null;
}

/**
 * @param wouldNotUpload The sentence a failed row wears when the server gave
 * no better one — a TR string, handed in so this hook stays free of the
 * translations.
 */
export function useAttachments(alertId: number, wouldNotUpload: string) {
  const [staged, setStaged] = useState<Staged[]>([]);
  const nextKey = useRef(0);

  /**
   * The bytes, kept until the row is removed, SO A FAILURE CAN BE RETRIED.
   *
   * This is a real cost and it is worth stating: a 100MB video is a ~133MB
   * base64 string, and holding it after the upload has finished is memory that
   * a phone does not have to spare. So it is dropped the moment the upload
   * lands and only survives for a row that FAILED — which is exactly the row
   * somebody is about to tap again.
   */
  const bytes = useRef(new Map<string, { kind: AttachKind; file: CapturedFile }>());

  /**
   * The in-flight upload per key, so the composer can await one after Send.
   *
   * Send does not wait for uploads — see `canSend` — which means the tray is
   * cleared while requests are still in the air. Their `setStaged` calls then
   * land on an empty list and do nothing, so without this the ids would simply
   * be lost: the bytes would be in Odoo, attached to nothing, invisible
   * forever. The promise is what lets the composer collect them and put them
   * on the message it just posted.
   */
  const jobs = useRef(new Map<string, Promise<Landed>>());

  /**
   * How each upload ENDED, kept after the job is gone.
   *
   * The race this closes: Send clears the tray, an upload lands a moment later
   * and writes its id into a row that is no longer there, and then the post
   * fails and the tray is restored from the snapshot taken before any of that
   * — putting back a row that says SENDING for a file which is sitting
   * finished in Odoo. It would never move again.
   */
  const landed = useRef(new Map<string, Landed>());

  /**
   * Revoke every preview when this unmounts.
   *
   * A `blob:` URL pins its bytes until revoked, and the composer is mounted per
   * andon — so a shift of opening and closing andons without this holds every
   * photo anybody looked at for the life of the tab.
   */
  const live = useRef(new Set<string>());
  useEffect(
    () => () => {
      for (const url of live.current) URL.revokeObjectURL(url);
      live.current.clear();
    },
    [],
  );

  const release = (urls: string[]) => {
    for (const url of urls) {
      URL.revokeObjectURL(url);
      live.current.delete(url);
    }
  };

  const forget = useCallback((key: string) => {
    bytes.current.delete(key);
    jobs.current.delete(key);
    landed.current.delete(key);
    setStaged((prev) => {
      const going = prev.filter((s) => s.key === key);
      release(previewsOf(going));
      return drop(prev, key);
    });
  }, []);

  /**
   * Empty the tray, WITHOUT cancelling what is in the air.
   *
   * Called the instant Send is tapped. The jobs map is deliberately left
   * alone: those uploads are still running and the composer is holding their
   * promises so it can attach them to the message it just posted. Only the
   * retained bytes go, and only for rows that had already landed — a row still
   * uploading may yet fail, and then it has nothing to retry from.
   */
  const clear = useCallback(() => {
    setStaged((prev) => {
      for (const row of prev) {
        if (row.id != null || row.error != null) bytes.current.delete(row.key);
      }
      release(previewsOf(prev));
      return [];
    });
  }, []);

  /**
   * One upload, from the bytes to the row. The only place `andonApi.attach`
   * is called from the composer.
   *
   * Shared by the first attempt and every retry, so the two cannot diverge —
   * which they would, because a retry is exactly the path nobody exercises
   * until something is already going wrong.
   */
  const upload = useCallback(
    (key: string, kind: AttachKind, file: CapturedFile): Promise<Landed> => {
      const job = (async (): Promise<Landed> => {
        try {
          const out = await andonApi.attach({
            alertId,
            kind,
            data: file.data,
            name: file.name,
            // Omitted rather than zeroed for anything without one: "no length"
            // and "zero length" are different facts, and a photo labelled 0:00
            // in the thread reads as a clip that failed to record.
            ...(file.seconds != null ? { seconds: Math.round(file.seconds) } : {}),
          });
          // The bytes have arrived, so the copy held for a retry is dead weight
          // — and on a 100MB clip it is a great deal of dead weight.
          bytes.current.delete(key);
          landed.current.set(key, { key, id: out.attachment.id, error: null });
          setStaged((prev) => settled(prev, key, out.attachment.id));
          return { key, id: out.attachment.id, error: null };
        } catch (err) {
          const message = err instanceof ApiError || err instanceof Error ? err.message : wouldNotUpload;
          landed.current.set(key, { key, id: null, error: message });
          setStaged((prev) => failed(prev, key, message));
          return { key, id: null, error: message };
        } finally {
          jobs.current.delete(key);
        }
      })();
      jobs.current.set(key, job);
      return job;
    },
    [alertId, wouldNotUpload],
  );

  /**
   * Stage one file and start its upload.
   *
   * Resolves when the upload settles either way. Callers do not await it — the
   * tray row is what the person sees — but a caller that wants to know when
   * everything has landed can.
   */
  const add = useCallback(
    async (kind: AttachKind, file: CapturedFile, localBlob?: Blob): Promise<void> => {
      const key = `a${nextKey.current++}`;
      // Only the things that have something to show. A PDF's card carries its
      // name instead, and creating a URL for it would be one more to revoke.
      const preview = localBlob && (kind === 'photo' || kind === 'video') ? URL.createObjectURL(localBlob) : null;
      if (preview) live.current.add(preview);

      bytes.current.set(key, { kind, file });
      setStaged((prev) => stage(prev, { key, name: file.name, kind, bytes: file.size, seconds: file.seconds, preview }));

      await upload(key, kind, file);
    },
    [upload],
  );

  /**
   * TAP A FAILED FILE TO SEND IT AGAIN.
   *
   * The commonest failure on this floor is not a bad file, it is a bad thirty
   * seconds of uplink — and the only action a failed tile used to offer was
   * remove, which meant going back to the gallery to find a clip that is
   * already in the browser's memory. One tap instead.
   *
   * Silently ignored when there are no bytes to retry from, which is the case
   * for a row restored after a failed SEND (those already have their ids) and
   * for anything whose bytes were dropped on the way out.
   */
  const retry = useCallback(
    (key: string) => {
      const held = bytes.current.get(key);
      if (!held || jobs.current.has(key)) return;
      // Back to uploading: the row has to stop reading FAILED or the second
      // attempt looks exactly like the first one still sitting there.
      landed.current.delete(key);
      setStaged((prev) => prev.map((s) => (s.key === key ? { ...s, error: null } : s)));
      void upload(key, held.kind, held.file);
    },
    [upload],
  );

  /**
   * Wait for these keys to settle, whatever the tray now says.
   *
   * Read from the JOBS map rather than from `staged`, because the caller is the
   * composer after Send and the tray is already empty by then. A key with no
   * job settled while the caller was getting here — the tray row it wrote is
   * gone with the rest, so its id would be unrecoverable; it is reported as a
   * miss rather than silently dropped.
   */
  const settle = useCallback(async (keys: string[]): Promise<Landed[]> => {
    const pending = keys.map(
      (key) => jobs.current.get(key) ?? Promise.resolve<Landed>(landed.current.get(key) ?? { key, id: null, error: null }),
    );
    return Promise.all(pending);
  }, []);

  /**
   * Stage something the server already holds — a picked GIF.
   *
   * No upload: the BFF fetched it, sniffed it and stored it, so it arrives
   * finished. It still goes through the tray so it travels with the message
   * and the caption like any other file.
   */
  const adopt = useCallback(
    (entry: { id: number; name: string; kind: AttachKind; bytes: number }) => {
      const key = `a${nextKey.current++}`;
      setStaged((prev) => adoptOne(prev, { key, ...entry, preview: andonApi.attachmentUrl(alertId, entry.id) }));
    },
    [alertId],
  );

  /**
   * Put a tray back exactly as it was.
   *
   * For one caller and one moment: the composer empties the tray the instant
   * Send is tapped, because the files are already drawn inside the bubble it
   * puts on the thread — and if that post then fails, they have to come back
   * rather than be quietly gone.
   *
   * RESTORED THROUGH `landed`, NOT STRAIGHT FROM THE SNAPSHOT. Send does not
   * wait for uploads, so the snapshot can contain rows that were mid-flight —
   * and by the time a failed post gets here those may have finished, into a
   * tray that was not there to receive them. Putting the snapshot back
   * verbatim would restore a row reading SENDING for a file already sitting in
   * Odoo, and nothing would ever move it again.
   */
  const restore = useCallback((list: Staged[]) => {
    setStaged(
      list.map((row) => {
        if (row.id != null || row.error != null) return row;
        const end = landed.current.get(row.key);
        return end ? { ...row, id: end.id, error: end.error } : row;
      }),
    );
  }, []);

  return {
    staged,
    add,
    adopt,
    restore,
    retry,
    settle,
    forget,
    clear,
    ids: idsOf(staged),
    uploading: isUploading(staged),
    failed: anyFailed(staged),
    full: staged.length >= MAX_STAGED,
  };
}
